import React, { useCallback, useEffect, useMemo, useState } from 'react';

import AdminRepository from '../../services/AdminRepository.js';

import SortOrderToggle from '../common/SortOrderToggle.jsx';

import StatisticsProductActivityCards from './StatisticsProductActivityCards.jsx';

import {

  buildStorageTypeRows,

  formatBytes,

  formatDateKey,

  formatNumber,

  isLegacyStorageByContentType,

} from '../../utils/statisticsFormat.js';

import './ClinicStatisticsLogsPanel.css';
import './StatisticsCharts.css';
import './StatisticsDetailShared.css';



const RANKING_LABELS = {

  byRegisteredPatients: '登録患者',

  byAppDocs: 'アプリDoc',

  byRecordCount: 'レコード',

  byStorageBytes: 'Storage',

};



function MetricBreakdownTable({ title, entries, valueFormatter = formatNumber }) {

  return (

    <div className="stats-detail-subsection">

      <h4>{title}</h4>

      {!entries?.length ? (
        <div className="stats-empty stats-detail-subsection__empty">データなし</div>
      ) : (
        <div className="stats-table-wrap">

          <table className="stats-table stats-table--compact">

            <thead>

              <tr>

                <th>項目</th>

                <th className="stats-table__num">値</th>

              </tr>

            </thead>

            <tbody>

              {entries.map((entry) => (

                <tr key={entry.key}>

                  <td>{entry.key}</td>

                  <td className="stats-table__num">{valueFormatter(entry.value)}</td>

                </tr>

              ))}

            </tbody>

          </table>

        </div>
      )}

    </div>

  );

}



function StorageTypeTable({ metrics }) {

  const rows = buildStorageTypeRows(metrics);

  const legacy = isLegacyStorageByContentType(metrics);



  return (

    <div className="stats-detail-subsection">

      <h4>{legacy ? 'Storage種別（旧形式・件数のみ）' : 'Storage種別'}</h4>

      {!rows.length ? (
        <div className="stats-empty stats-detail-subsection__empty">データなし</div>
      ) : (
        <div className="stats-table-wrap">

          <table className="stats-table stats-table--compact">

            <thead>

              <tr>

                <th>MIME種別</th>

                <th className="stats-table__num">ファイル数</th>

                <th className="stats-table__num">合計サイズ</th>

                <th className="stats-table__num">平均サイズ</th>

              </tr>

            </thead>

            <tbody>

              {rows.map((row) => (

                <tr key={row.contentType}>

                  <td>{row.contentType}</td>

                  <td className="stats-table__num">{formatNumber(row.files)}</td>

                  <td className="stats-table__num">{legacy && row.mode === 'legacyCount' ? '—' : formatBytes(row.bytes)}</td>

                  <td className="stats-table__num">{legacy && row.mode === 'legacyCount' ? '—' : formatBytes(row.avgBytes)}</td>

                </tr>

              ))}

            </tbody>

          </table>

        </div>
      )}

    </div>

  );

}



function flattenNumericObject(obj, prefix = '') {

  const entries = [];

  Object.entries(obj || {}).forEach(([key, value]) => {

    const label = prefix ? `${prefix}.${key}` : key;

    if (typeof value === 'number') {

      entries.push({ key: label, value });

      return;

    }

    if (value && typeof value === 'object' && !Array.isArray(value)) {

      if ('files' in value && 'bytes' in value) return;

      entries.push(...flattenNumericObject(value, label));

    }

  });

  return entries.sort((a, b) => b.value - a.value || a.key.localeCompare(b.key, 'ja'));

}



export function StatisticsClinicAppDetail({ log }) {

  const metrics = log?.metrics || {};

  const adminMeta = log?.adminHubMeta || {};

  const completeness = log?.sourceCompleteness || {};



  return (

    <div className="stats-clinic-app-detail">

      <div className="stats-detail-metric-grid">

        <div><span>登録患者</span><strong>{formatNumber(metrics.patient?.registeredPatients)}</strong></div>

        <div><span>新規患者</span><strong>{formatNumber(metrics.patient?.createdOnTargetDate)}</strong></div>

        <div><span>アプリDoc</span><strong>{formatNumber(metrics.app?.appDocs)}</strong></div>

        <div><span>レコード</span><strong>{formatNumber(metrics.records?.total)}</strong></div>

        <div><span>Storage</span><strong>{formatBytes(metrics.media?.storageBytes)}</strong></div>

        <div><span>更新数</span><strong>{formatNumber(metrics.usage?.firestoreUpdatesOnTargetDate)}</strong></div>

        <div><span>AdminHubユーザー</span><strong>{formatNumber(adminMeta.userTotal)}</strong></div>

      </div>



      <MetricBreakdownTable

        title="患者属性"

        entries={flattenNumericObject(metrics.patient?.byGender, 'gender')}

      />

      <MetricBreakdownTable

        title="レコード種別"

        entries={flattenNumericObject(metrics.records?.bySubcollection)}

      />

      <StorageTypeTable metrics={metrics} />

      <MetricBreakdownTable

        title="時間帯別更新"

        entries={flattenNumericObject(metrics.usage?.byHour, 'hour')}

      />



      {Object.keys(completeness).length > 0 && (

        <div className="stats-detail-subsection">

          <h4>ソース完全性</h4>

          <ul className="stats-detail-list">

            <li>Firestore: {completeness.patientsdataFirestore === false ? '一部失敗' : 'OK'}</li>

            <li>Storage: {completeness.patientsdataStorage === false ? '一部失敗' : 'OK'}</li>

            <li>AdminHub契約: {completeness.adminHubContracts === false ? '一部失敗' : 'OK'}</li>

          </ul>

        </div>

      )}

    </div>

  );

}



export function StatisticsProductDetailPanel({

  productKey,

  targetDateKey,

  onBack,

}) {

  const repo = useMemo(() => AdminRepository.getInstance(), []);

  const [detail, setDetail] = useState(null);

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState('');

  const [search, setSearch] = useState('');

  const [minPatients, setMinPatients] = useState('');

  const [minRecords, setMinRecords] = useState('');

  const [activeOnly, setActiveOnly] = useState(false);

  const [sortKey, setSortKey] = useState('registeredPatients');

  const [sortOrder, setSortOrder] = useState('desc');

  const [expandedClinicId, setExpandedClinicId] = useState(null);



  const loadDetail = useCallback(async () => {

    if (!productKey) return;

    setLoading(true);

    setError('');

    try {

      const data = await repo.getStatisticsProductDetail(productKey, {

        targetDateKey,

        search,

        minPatients,

        minRecords,

        activeOnly: activeOnly ? '1' : '',

        sort: sortKey,

        order: sortOrder,

      });

      setDetail(data);

    } catch (err) {

      setError(err.message || '製品詳細の取得に失敗しました');

    } finally {

      setLoading(false);

    }

  }, [repo, productKey, targetDateKey, search, minPatients, minRecords, activeOnly, sortKey, sortOrder]);



  useEffect(() => {

    loadDetail();

  }, [loadDetail]);



  const product = detail?.product;

  const total = product?.metrics?.total || {};

  const rankings = product?.rankings || {};

  const byFeature = flattenNumericObject(product?.metrics?.byFeature);

  const byHour = flattenNumericObject(product?.metrics?.byHour, 'hour');



  return (

    <section className="stats-section stats-product-detail">

      <div className="stats-section-title stats-product-detail-header">

        <div>

          <button type="button" className="stats-detail-back" onClick={onBack}>

            ← 製品一覧に戻る

          </button>

          <h2>
            {productKey}
            <span className="stats-product-key-badge">製品</span>
          </h2>

          <p className="stats-detail-meta">

            対象日 {formatDateKey(detail?.targetDateKey || targetDateKey)} /

            医院 {formatNumber(detail?.counts?.filtered)} / {formatNumber(detail?.counts?.total)} 件

          </p>

        </div>

      </div>



      {error && <div className="stats-alert danger stats-detail-alert">{error}</div>}



      {loading ? (

        <div className="stats-empty">製品詳細を読み込んでいます...</div>

      ) : !product ? (

        <div className="stats-empty">製品ログが見つかりません。</div>

      ) : (

        <>

          <div className="stats-detail-summary-grid">

            <div className="stats-metric-card">

              <span>連携医院（Docs）</span>

              <strong>{formatNumber(product.sourceClinicCount)}</strong>

              <small>アクティブ: {formatNumber(product.activeClinicCount)}</small>

            </div>

            <div className="stats-metric-card">

              <span>ライセンス保有医院</span>

              <strong>{formatNumber(product.licensedClinicCount ?? 0)}</strong>

            </div>

            <div className="stats-metric-card">

              <span>登録患者</span>

              <strong>{formatNumber(total.patient?.registeredPatients)}</strong>

            </div>

            <div className="stats-metric-card">

              <span>新規患者</span>

              <strong>{formatNumber(product.activity?.newPatients ?? total.patient?.createdOnTargetDate)}</strong>

            </div>

            <div className="stats-metric-card">

              <span>レコード</span>

              <strong>{formatNumber(total.records?.total)}</strong>

            </div>

            <div className="stats-metric-card">

              <span>Storage</span>

              <strong>{formatBytes(total.media?.storageBytes)}</strong>

            </div>

          </div>



          <div className="stats-activity-section">

            <div className="stats-section-title">

              <div>

                <h3>日次アクティビティ</h3>

                <p>カードをクリックすると時系列グラフと一覧を表示します。</p>

              </div>

            </div>

            <StatisticsProductActivityCards

              product={product}

              productKey={productKey}

              targetDateKey={detail?.targetDateKey || targetDateKey}

            />

          </div>



          <div className="stats-detail-rankings-header">
            <h3>医院別上位</h3>
            <p>指標ごとに上位10医院を表示します。</p>
          </div>

          <div className="stats-detail-rankings">

            {Object.entries(RANKING_LABELS).map(([key, label]) => (

              <div key={key} className="stats-detail-ranking-card">

                <h4>{label} 上位</h4>

                <ol>

                  {(rankings[key] || []).map((entry) => (

                    <li key={`${key}-${entry.clinicId}`}>

                      <span>{entry.clinicNameSnapshot || entry.clinicId}</span>

                      <strong>{key === 'byStorageBytes' ? formatBytes(entry.value) : formatNumber(entry.value)}</strong>

                    </li>

                  ))}

                  {(rankings[key] || []).length === 0 && <li className="muted">データなし</li>}

                </ol>

              </div>

            ))}

          </div>



          <div className="stats-detail-split">

            <MetricBreakdownTable title="機能別レコード" entries={byFeature} />

            <MetricBreakdownTable title="時間帯別利用" entries={byHour} />

          </div>



          <div className="stats-detail-clinic-section">

            <div className="stats-section-title">

              <div>

                <h3>医院別内訳</h3>

                <p>製品 {productKey} を利用する医院の日次ログです。</p>

              </div>

            </div>



            <div className="list-toolbar stats-detail-toolbar">

              <div className="search-bar list-toolbar__search">

                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">

                  <circle cx="11" cy="11" r="8" />

                  <path d="m21 21-4.35-4.35" />

                </svg>

                <input

                  type="text"

                  placeholder="医院名で検索..."

                  value={search}

                  onChange={(e) => setSearch(e.target.value)}

                />

              </div>

              <input

                type="number"

                min="0"

                className="stats-filter-input"

                placeholder="患者数下限"

                value={minPatients}

                onChange={(e) => setMinPatients(e.target.value)}

                aria-label="患者数下限"

              />

              <input

                type="number"

                min="0"

                className="stats-filter-input"

                placeholder="レコード下限"

                value={minRecords}

                onChange={(e) => setMinRecords(e.target.value)}

                aria-label="レコード下限"

              />

              <label className="stats-filter-checkbox">

                <input

                  type="checkbox"

                  checked={activeOnly}

                  onChange={(e) => setActiveOnly(e.target.checked)}

                />

                アクティブのみ

              </label>

              <div className="list-toolbar__sort">

                <span className="list-toolbar__sort-label">並び替え</span>

                <select

                  className="admin-select"

                  value={sortKey}

                  onChange={(e) => setSortKey(e.target.value)}

                  aria-label="並び替え項目"

                >

                  <option value="registeredPatients">登録患者</option>

                  <option value="newPatients">新規患者</option>

                  <option value="records">レコード</option>

                  <option value="storageBytes">Storage</option>

                  <option value="updates">更新数</option>

                  <option value="clinicName">医院名</option>

                </select>

                <SortOrderToggle

                  order={sortOrder}

                  onToggle={() => setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))}

                />

              </div>

            </div>



            <div className="stats-table-wrap">

              <table className="stats-table">

                <thead>

                  <tr>

                    <th aria-hidden="true" />

                    <th>医院</th>

                    <th>登録患者</th>

                    <th>新規患者</th>

                    <th>レコード</th>

                    <th>Storage</th>

                    <th>更新数</th>

                  </tr>

                </thead>

                <tbody>

                  {(detail?.clinicAppDaily || []).map((log) => {

                    const rowKey = log.id || `${log.clinicId}-${log.productKey}`;

                    const isExpanded = expandedClinicId === rowKey;

                    return (

                      <React.Fragment key={rowKey}>

                        <tr

                          className={`stats-expandable-row${isExpanded ? ' is-expanded' : ''}`}

                          onClick={() => setExpandedClinicId(isExpanded ? null : rowKey)}

                        >

                          <td className="stats-expand-icon">{isExpanded ? '▼' : '▶'}</td>

                          <td>

                            <strong>{log.clinicNameSnapshot || log.clinicId}</strong>

                            <small>{log.clinicId}</small>

                          </td>

                          <td>{formatNumber(log.metrics?.patient?.registeredPatients)}</td>

                          <td>{formatNumber(log.metrics?.patient?.createdOnTargetDate)}</td>

                          <td>{formatNumber(log.metrics?.records?.total)}</td>

                          <td>{formatBytes(log.metrics?.media?.storageBytes)}</td>

                          <td>{formatNumber(log.metrics?.usage?.firestoreUpdatesOnTargetDate)}</td>

                        </tr>

                        {isExpanded && (

                          <tr className="stats-expanded-detail-row">

                            <td colSpan="7">

                              <StatisticsClinicAppDetail log={log} />

                            </td>

                          </tr>

                        )}

                      </React.Fragment>

                    );

                  })}

                  {(detail?.clinicAppDaily || []).length === 0 && (

                    <tr><td colSpan="7">条件に一致する医院ログがありません。</td></tr>

                  )}

                </tbody>

              </table>

            </div>

          </div>

        </>

      )}

    </section>

  );

}



const ClinicStatisticsLogsPanel = ({ clinicId, clinicName }) => {

  const repo = useMemo(() => AdminRepository.getInstance(), []);

  const [data, setData] = useState(null);

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState('');

  const [productFilter, setProductFilter] = useState('all');

  const [search, setSearch] = useState('');

  const [minPatients, setMinPatients] = useState('');

  const [sortKey, setSortKey] = useState('productKey');

  const [sortOrder, setSortOrder] = useState('asc');

  const [expandedKey, setExpandedKey] = useState(null);



  const loadLogs = useCallback(async () => {

    if (!clinicId) return;

    setLoading(true);

    setError('');

    try {

      const result = await repo.getClinicStatisticsLogs(clinicId, {

        search,

        minPatients,

        productKey: productFilter === 'all' ? '' : productFilter,

        sort: sortKey,

        order: sortOrder,

      });

      setData(result);

    } catch (err) {

      setError(err.message || '医院別統計ログの取得に失敗しました');

    } finally {

      setLoading(false);

    }

  }, [repo, clinicId, search, minPatients, productFilter, sortKey, sortOrder]);



  useEffect(() => {

    loadLogs();

  }, [loadLogs]);



  const productKeys = data?.productKeys || [];



  return (

    <section className="clinic-detail-panel clinic-detail-panel--wide clinic-statistics-panel">

      <div className="clinic-detail-panel-header">

        <div>

          <h2>利用統計ログ</h2>

          <p>

            {clinicName || data?.clinicNameSnapshot || clinicId} の日次統計ログ

            {data?.targetDateKey ? `（${formatDateKey(data.targetDateKey)}）` : ''}

          </p>

        </div>

        <span className="badge info">{formatNumber(data?.counts?.filtered)} 件</span>

      </div>



      {error && <div className="clinic-detail-alert danger">{error}</div>}



      {!data?.targetDateKey && !loading ? (

        <div className="clinic-detail-empty">統計ログがまだ作成されていません。</div>

      ) : (

        <>

          <div className="list-toolbar stats-detail-toolbar">

            <div className="search-bar list-toolbar__search">

              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">

                <circle cx="11" cy="11" r="8" />

                <path d="m21 21-4.35-4.35" />

              </svg>

              <input

                type="text"

                placeholder="製品名で検索..."

                value={search}

                onChange={(e) => setSearch(e.target.value)}

              />

            </div>

            <select

              className="admin-select"

              value={productFilter}

              onChange={(e) => setProductFilter(e.target.value)}

              aria-label="製品で絞り込み"

            >

              <option value="all">すべての製品</option>

              {productKeys.map((key) => (

                <option key={key} value={key}>{key}</option>

              ))}

            </select>

            <input

              type="number"

              min="0"

              className="stats-filter-input"

              placeholder="患者数下限"

              value={minPatients}

              onChange={(e) => setMinPatients(e.target.value)}

              aria-label="患者数下限"

            />

            <div className="list-toolbar__sort">

              <span className="list-toolbar__sort-label">並び替え</span>

              <select

                className="admin-select"

                value={sortKey}

                onChange={(e) => setSortKey(e.target.value)}

                aria-label="並び替え項目"

              >

                <option value="productKey">製品</option>

                <option value="registeredPatients">登録患者</option>

                <option value="newPatients">新規患者</option>

                <option value="records">レコード</option>

                <option value="storageBytes">Storage</option>

                <option value="updates">更新数</option>

              </select>

              <SortOrderToggle

                order={sortOrder}

                onToggle={() => setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))}

              />

            </div>

            <button type="button" className="btn btn-secondary btn-sm" onClick={loadLogs} disabled={loading}>

              再読み込み

            </button>

          </div>



          {loading ? (

            <div className="clinic-detail-empty">統計ログを読み込んでいます...</div>

          ) : (data?.clinicAppDaily || []).length === 0 ? (

            <div className="clinic-detail-empty">この医院の統計ログはありません。</div>

          ) : (

            <div className="clinic-statistics-log-list">

              {(data?.clinicAppDaily || []).map((log) => {

                const rowKey = log.id || `${log.clinicId}-${log.productKey}`;

                const isExpanded = expandedKey === rowKey;

                return (

                  <article

                    key={rowKey}

                    className={`clinic-statistics-log-card${isExpanded ? ' is-expanded' : ''}`}

                  >

                    <button

                      type="button"

                      className="clinic-statistics-log-card__header"

                      onClick={() => setExpandedKey(isExpanded ? null : rowKey)}

                    >

                      <div>

                        <strong>{log.productKey}</strong>

                        <span>{formatDateKey(log.targetDateKey)}</span>

                      </div>

                      <div className="clinic-statistics-log-card__metrics">

                        <span>患者 {formatNumber(log.metrics?.patient?.registeredPatients)}</span>

                        <span>新規 {formatNumber(log.metrics?.patient?.createdOnTargetDate)}</span>

                        <span>レコード {formatNumber(log.metrics?.records?.total)}</span>

                        <span>{formatBytes(log.metrics?.media?.storageBytes)}</span>

                      </div>

                      <span className="clinic-statistics-log-card__toggle">{isExpanded ? '▼' : '▶'}</span>

                    </button>

                    {isExpanded && (

                      <div className="clinic-statistics-log-card__body">

                        <StatisticsClinicAppDetail log={log} />

                      </div>

                    )}

                  </article>

                );

              })}

            </div>

          )}

        </>

      )}

    </section>

  );

};



export default ClinicStatisticsLogsPanel;

